/**
 * Utilitaires pour les animations et effets visuels de la section Formations
 */
export class FormationsEffects {
  /**
   * Configure le système d'onglets pour la section formations
   */
  public static setupTabSystem(element: HTMLElement, onTabActivated?: (index: number) => void): () => void {
    const tabButtons = element.querySelectorAll('.formation-tab-btn');
    const tabContents = element.querySelectorAll('[data-formation-content]');
    const tabShell = element.querySelector('.formation-tabs-shell') as HTMLElement | null;
    const tabScroll = element.querySelector('.formation-tabs-scroll') as HTMLElement | null;
    const cleanupFunctions: Array<() => void> = [];

    const updateScrollIndicators = () => {
      if (!tabShell || !tabScroll) return;

      const scrollable = tabScroll.scrollWidth - tabScroll.clientWidth > 4;
      tabShell.classList.toggle('is-scrollable', scrollable);

      if (!scrollable) {
        tabShell.classList.remove('scroll-left', 'scroll-right');
        return;
      }

      const atStart = tabScroll.scrollLeft <= 2;
      const atEnd = tabScroll.scrollLeft + tabScroll.clientWidth >= tabScroll.scrollWidth - 2;

      tabShell.classList.toggle('scroll-left', !atStart);
      tabShell.classList.toggle('scroll-right', !atEnd);
    };

    if (tabScroll) {
      const handleScroll = () => updateScrollIndicators();
      tabScroll.addEventListener('scroll', handleScroll, { passive: true });
      cleanupFunctions.push(() => tabScroll.removeEventListener('scroll', handleScroll));
    }

    const handleResize = () => updateScrollIndicators();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    cleanupFunctions.push(() => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    });

    // Fonction pour activer un onglet
    const activateTab = (index: number) => {
      // Mettre à jour les boutons
      tabButtons.forEach((btn, i) => {
        const button = btn as HTMLElement;
        if (i === index) {
          button.classList.add('active-tab', 'bg-gradient-to-r', 'from-[#ed4c67]/20', 'to-[#3498db]/20', 'text-white', 'border', 'border-white/20');
          button.classList.remove('text-white/80');
        } else {
          button.classList.remove('active-tab', 'bg-gradient-to-r', 'from-[#ed4c67]/20', 'to-[#3498db]/20', 'text-white', 'border', 'border-white/20');
          button.classList.add('text-white/80');
        }
      });

      // Cacher tous les contenus d'abord
      tabContents.forEach(content => {
        const contentElement = content as HTMLElement;
        contentElement.classList.remove('active');
        setTimeout(() => {
          contentElement.classList.add('hidden');
        }, 300);
      });

      // Afficher le contenu sélectionné
      setTimeout(() => {
        const selectedContent = tabContents[index] as HTMLElement;
        if (selectedContent) {
          selectedContent.classList.remove('hidden');
          // Attendre que le navigateur traite le changement de visibilité
          setTimeout(() => {
            selectedContent.classList.add('active');
          }, 50);
        }
      }, 350);

      // Appeler le callback si défini
      if (onTabActivated) {
        onTabActivated(index);
      }
    };

    // Ajouter les écouteurs d'événements pour les onglets
    tabButtons.forEach((button, index) => {
      const clickHandler = () => {
        activateTab(index);
      };
      
      button.addEventListener('click', clickHandler);

      // Ajouter au nettoyage
      cleanupFunctions.push(() => {
        button.removeEventListener('click', clickHandler);
      });
    });

    // Activer le premier onglet par défaut
    setTimeout(() => {
      activateTab(0);
      updateScrollIndicators();
    }, 100);

    setTimeout(updateScrollIndicators, 250);

    // Retourner une fonction pour nettoyer tous les écouteurs d'événements
    return () => {
      cleanupFunctions.forEach(fn => fn());
    };
  }

  /**
   * Configure les animations au défilement pour la section formations
   */
  public static setupScrollAnimations(element: HTMLElement): () => void {
    const handleScroll = () => {
      // querySelectorAll ne supporte pas les classes avec '/'
      const animatedElements = element.querySelectorAll(
        '.fade-in-up, [class*="bg-white/10"], [class*="bg-gradient-to-r"], .contact-btn'
      );
      animatedElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.9;

        if (isVisible) {
          setTimeout(() => {
            (element as HTMLElement).classList.add('visible-animation');
          }, index * 50);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    // Déclencher une fois pour les éléments déjà visibles
    setTimeout(handleScroll, 300);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }

  /**
   * Configure les effets de parallaxe pour la section formations
   */
  public static setupParallaxEffect(element: HTMLElement): () => void {
    const parallaxElements = element.querySelectorAll('.wave-anim, .wave-anim-alt, .radiate');

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;

      parallaxElements.forEach(element => {
        const el = element as HTMLElement;
        const depth = el.classList.contains('radiate') ? 0.05 :
                     el.classList.contains('wave-anim') ? 0.03 : 0.02;
        const moveX = (clientX - centerX) * depth;
        const moveY = (clientY - centerY) * depth;

        // Effet de parallaxe plus subtil
        el.style.transform = `translate(${moveX}px, ${moveY}px)`;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }
}
